import { useState, useEffect } from 'react';
import SettingsForm from './components/SettingsForm';
import GiftCardsManager from './components/GiftCards/GiftCardsManager';
import CustomersManager from './components/CustomersManager';
import { api, getAccountId } from './services/api';
import { logger } from './utils/logger';
import { useToast } from './hooks/useToast';
import { ToastContainer } from './components/ui/toast';
import './App.css';

type Tab = 'settings' | 'giftcards' | 'customers';

function App() {
  const [activeTab, setActiveTab] = useState<Tab>('settings');
  const [isActivated, setIsActivated] = useState(false);
  const [customerSearchType, setCustomerSearchType] = useState<'internal' | 'external'>('internal');
  const { toasts, removeToast } = useToast();

  // Проверяем активацию решения при загрузке
  useEffect(() => {
    const checkActivation = async () => {
      try {
        const accountId = await getAccountId();
        if (accountId) {
          const settings = await api.getSettings(accountId);
          const activated = !!(settings.baseUrl);
          setIsActivated(activated);
          
          if (settings.customerSearchType) {
            setCustomerSearchType(settings.customerSearchType);
          }
          
          // Если решение активировано, устанавливаем активной вкладку Сертификаты
          if (activated) {
            setActiveTab('giftcards');
          }
        }
      } catch (err) {
        logger.error('Ошибка при проверке активации', err);
        setIsActivated(false);
      }
    };
    
    checkActivation();
    
    // Слушаем событие активации от SettingsForm
    const handleActivation = (event: CustomEvent) => {
      setIsActivated(true);
      if (event.detail?.customerSearchType) {
        setCustomerSearchType(event.detail.customerSearchType);
      }
      // Не переключаемся автоматически на вкладку Сертификаты, остаемся на Настройках
    };
    
    window.addEventListener('solutionActivated', handleActivation as EventListener);
    
    return () => {
      window.removeEventListener('solutionActivated', handleActivation as EventListener);
    };
  }, []);

  // Определяем, какие вкладки показывать
  const showGiftCards = isActivated;
  const showCustomers = isActivated && customerSearchType === 'external';
  const showSettings = true; // Всегда показываем настройки

  // Если текущая вкладка стала недоступной, переключаемся на доступную
  useEffect(() => {
    if (activeTab === 'customers' && !showCustomers) {
      // Если вкладка Покупатели стала недоступной, переключаемся на Сертификаты или Настройки
      setActiveTab(showGiftCards ? 'giftcards' : 'settings');
    } else if (activeTab === 'giftcards' && !showGiftCards) {
      // Если вкладка Сертификаты стала недоступной, переключаемся на Настройки
      setActiveTab('settings');
    }
  }, [activeTab, showGiftCards, showCustomers]);

  return (
    <div className="app">
      <nav className="app-nav">
        {showGiftCards && (
          <button
            className={activeTab === 'giftcards' ? 'active' : ''}
            onClick={() => setActiveTab('giftcards')}
          >
            Сертификаты
          </button>
        )}
        {showCustomers && (
          <button
            className={activeTab === 'customers' ? 'active' : ''}
            onClick={() => setActiveTab('customers')}
          >
            Покупатели
          </button>
        )}
        {showSettings && (
          <button
            className={activeTab === 'settings' ? 'active' : ''}
            onClick={() => setActiveTab('settings')}
          >
            Настройки
          </button>
        )}
      </nav>

      <div className="app-content">
        {activeTab === 'settings' && <SettingsForm />}
        {activeTab === 'giftcards' && showGiftCards && <GiftCardsManager />}
        {activeTab === 'customers' && showCustomers && <CustomersManager />}
      </div>
      <ToastContainer toasts={toasts} onClose={removeToast} />
    </div>
  );
}

export default App;

